import {
  CatalogueProduct,
  CheckoutRequest,
  CheckoutSessionResponse,
  CreditPack,
  CreditPacksResponse,
} from "../types/billing";
import { readProductCatalogue } from "../utils/productCatalogue";
import { createAppBackendClient, isAppBackendConfigured } from "./appBackend";

/**
 * Buying Credits: the calls behind the Buy Credits dialog and the product costs the pages
 * quote. Errors travel as they arrive; the caller turns them into something to read with
 * `appBackendErrorOf`.
 */

/**
 * Short, because the page waits on this before it can show the section, and a backend that
 * hangs should leave the page looking as it does without billing rather than spinning.
 */
const PACKS_TIMEOUT_MS = 10000;

/**
 * Short for the same reason: the product cards wait on it, and a backend that hangs is
 * answered from the fallback table rather than left spinning.
 */
const PRODUCTS_TIMEOUT_MS = 10000;

/** Longer: the backend is creating a Stripe Checkout Session while the browser waits. */
const CHECKOUT_TIMEOUT_MS = 30000;

/** False when this deployment has no app-backend, and so nothing to buy Credits through. */
export const isBillingAvailable = (): boolean => isAppBackendConfigured();

/** The packs on sale, in the order the backend sends them: by credits, ascending. */
export const getCreditPacks = async (): Promise<CreditPack[]> => {
  const api = createAppBackendClient(PACKS_TIMEOUT_MS);
  const response = await api.get<CreditPacksResponse>("/billing/packs");
  return response.data?.packs ?? [];
};

/**
 * Every product the backend knows and what a run of it costs, in the order it sends them, with
 * `purchase` set on those that can be bought per product right now. Throws when the answer does
 * not read as a catalogue, so the caller treats it as it treats a backend it cannot reach.
 */
export const getProducts = async (): Promise<CatalogueProduct[]> => {
  const api = createAppBackendClient(PRODUCTS_TIMEOUT_MS);
  const response = await api.get<unknown>("/billing/products");
  return readProductCatalogue(response.data);
};

/**
 * Open a checkout and return Stripe's hosted page to send the browser to. A pack goes as its
 * lookup key alone; a product purchase adds its quantity and, for Multi-Entity SignalScan, the
 * extra companies per scan. Lookup keys are opaque: they go back exactly as they arrived. Only
 * the fields the request carries are sent, so a pack's body is what it has always been.
 */
export const startCheckout = async (
  request: CheckoutRequest,
): Promise<string> => {
  const body: CheckoutRequest = { lookup_key: request.lookup_key };
  if (request.quantity !== undefined) body.quantity = request.quantity;
  if (request.addon_quantity_per_unit !== undefined) {
    body.addon_quantity_per_unit = request.addon_quantity_per_unit;
  }
  if (request.return_product !== undefined) {
    body.return_product = request.return_product;
  }

  const api = createAppBackendClient(CHECKOUT_TIMEOUT_MS);
  const response = await api.post<CheckoutSessionResponse>(
    "/billing/checkout",
    body,
  );
  return response.data.url;
};
